import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";

function formatPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function Login() {
  const { login, loginByPhone } = useAuth();
  const [mode, setMode] = useState<"phone" | "email">("phone");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await loginByPhone(phone.replace(/\D/g, ""), pin);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || "Invalid phone or PIN");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    border: "2px solid var(--rbw-border)",
    background: "var(--rbw-bg-card)",
    color: "var(--rbw-navy)",
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--rbw-navy)" }}>
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20.5h-2zM0 20h2v20H0V20zm4 0h2v20H4V20zm4 0h2v20H8V20zm4 0h2v20h-2V20zm4 0h2v20h-2V20zm4 4h20v2H24v-2zm0 4h20v2H24v-2zm0 4h20v2H24v-2zm0 4h20v2H24v-2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
        }} />
        <div className="relative z-10 text-center">
          <img
            src="/images/logo-primary.jpg"
            alt="Rieker Bottle Works"
            className="w-72 mx-auto rounded-2xl shadow-2xl mb-10"
          />
          <h2 className="text-2xl font-light mb-3" style={{ color: "var(--rbw-gray-light)" }}>
            Store Management Platform
          </h2>
          <p className="text-sm max-w-sm mx-auto leading-relaxed" style={{ color: "var(--rbw-gray-dark)" }}>
            Inventory, purchasing, pricing, and point of sale — built for how Rieker actually runs.
          </p>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center p-8" style={{ background: "var(--rbw-bg)" }}>
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/images/logo-primary.jpg" alt="RBW" className="w-40 mx-auto rounded-xl shadow-lg" />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>
              Welcome back
            </h1>
            <p className="mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
              {mode === "phone" ? "Sign in with your cell number and PIN" : "Sign in to your account"}
            </p>
          </div>

          {error && (
            <div
              className="px-4 py-3 rounded-xl text-sm font-medium mb-5"
              style={{ background: "#FEE2E2", color: "var(--rbw-danger)", border: "1px solid #FECACA" }}
            >
              {error}
            </div>
          )}

          {mode === "phone" ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  Cell phone number
                </label>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                  className="w-full px-4 py-3 rounded-xl text-base outline-none"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                  placeholder="(717) 555-1234"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  4-digit PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="w-full px-4 py-3 rounded-xl text-2xl tracking-[0.5em] text-center outline-none"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                  placeholder="&bull;&bull;&bull;&bull;"
                />
              </div>

              <button
                type="submit"
                disabled={loading || phone.replace(/\D/g, "").length !== 10 || pin.length !== 4}
                className="w-full py-3.5 rounded-xl font-bold text-base disabled:opacity-50 active:scale-[0.98]"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>

              <button
                type="button"
                onClick={() => { setMode("email"); setError(""); }}
                className="w-full text-sm font-medium"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                Use email &amp; password instead
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl text-base outline-none"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                  placeholder="you@rieker.com"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl text-base outline-none"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-bold text-base disabled:opacity-50 active:scale-[0.98]"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>

              <button
                type="button"
                onClick={() => { setMode("phone"); setError(""); }}
                className="w-full text-sm font-medium"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                Use phone &amp; PIN instead
              </button>
            </form>
          )}

          <p className="text-center mt-8 text-xs" style={{ color: "var(--rbw-gray)" }}>
            Rieker Bottle Works &middot; Lancaster, Pennsylvania
          </p>
        </div>
      </div>
    </div>
  );
}
